/**
 * SharePage.jsx — public, read-only view of a shared Rammy conversation.
 *
 * Route: `/share/:token`
 *
 * Hits GET /api/public/share/{token} (no auth). Renders a warm, mobile-first
 * page with the exchange plus a "Try Rammy yourself" CTA — this is the viral
 * loop for rammy.ai. Deliberately minimal chrome so the conversation
 * itself is the hero.
 */
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import * as L from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function SharePage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/public/share/${token}`);
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.status === 404 ? "not-found" : "error");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  useEffect(() => {
    if (data?.title) document.title = `${data.title} · Rammy`;
    return () => { document.title = "Rammy"; };
  }, [data?.title]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--rammy-bg)] text-[var(--rammy-text-muted)] text-sm" data-testid="share-loading">
        <L.Loader2 size={16} className="animate-spin mr-2"/> Loading…
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--rammy-bg)] px-6" data-testid="share-error">
        <div className="max-w-md text-center space-y-4">
          <div className="w-14 h-14 rounded-2xl bg-[var(--rm-ink-900)] mx-auto flex items-center justify-center">
            <L.Ghost size={26} className="text-white"/>
          </div>
          <h1 className="text-2xl font-display">
            {error === "not-found" ? "This link is no longer live." : "Something went sideways."}
          </h1>
          <p className="text-sm text-[var(--rammy-text-sec)]">
            {error === "not-found"
              ? "The owner may have revoked it, or the conversation was deleted."
              : "Try again in a moment."}
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 rounded-full bg-[var(--rm-ink-900)] text-white px-5 py-2.5 text-sm font-medium hover:opacity-90 transition"
            data-testid="share-cta"
          >
            Meet Rammy <L.ArrowRight size={14}/>
          </Link>
        </div>
      </div>
    );
  }

  const messages = data?.messages || [];
  const shareDate = data?.created_at ? new Date(data.created_at) : null;

  return (
    <div className="min-h-screen bg-[var(--rammy-bg)] pb-20" data-testid="share-page">
      {/* Header */}
      <div className="max-w-2xl mx-auto px-5 pt-8 md:pt-14">
        <Link to="/" className="inline-flex items-center gap-2 text-[var(--rammy-text-sec)] hover:text-[var(--rammy-text)] transition" data-testid="share-brand">
          <div className="w-8 h-8 rounded-xl bg-[var(--rm-ink-900)] flex items-center justify-center">
            <span className="text-[var(--rammy-cyan)] text-[13px] font-bold">N</span>
          </div>
          <span className="text-sm font-medium">Rammy</span>
        </Link>

        <div className="mt-8 space-y-2">
          <div className="text-[10px] uppercase tracking-widest font-semibold text-[var(--rammy-text-muted)]">
            Shared by {data.owner_name}{shareDate ? ` · ${shareDate.toLocaleDateString(undefined,{month:"short",day:"numeric"})}` : ""}
          </div>
          <h1 className="font-display text-3xl md:text-4xl leading-tight" data-testid="share-title">
            {data.title}
          </h1>
        </div>
      </div>

      {/* Messages */}
      <div className="max-w-2xl mx-auto px-5 mt-8 space-y-4" data-testid="share-messages">
        {messages.length === 0 && (
          <div className="text-sm text-[var(--rammy-text-muted)]">This conversation is empty.</div>
        )}
        {messages.map((m, i) => (
          <div
            key={i}
            className={`rounded-2xl px-4 py-3 max-w-[85%] ${
              m.role === "user"
                ? "ml-auto bg-[var(--rammy-cyan)]/15 text-[var(--rammy-text)]"
                : "mr-auto bg-[var(--rm-surface)] border border-[var(--rammy-border)] text-[var(--rammy-text)]"
            }`}
            data-testid={`share-msg-${i}`}
          >
            <div className="text-[10px] uppercase tracking-widest font-semibold text-[var(--rammy-text-muted)] mb-1">
              {m.role === "user" ? data.owner_name : "Rammy"}
            </div>
            <div className="text-[15px] leading-relaxed whitespace-pre-wrap">{m.text}</div>
          </div>
        ))}
      </div>

      {/* CTA */}
      <div className="max-w-2xl mx-auto px-5 mt-12">
        <div className="rounded-3xl bg-[var(--rm-ink-900)] text-white p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center gap-5">
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest font-semibold text-white/50 mb-2">
              Want your own?
            </div>
            <div className="font-display text-2xl leading-snug">
              Meet Rammy — your voice-first AI companion.
            </div>
            <div className="text-sm text-white/70 mt-1.5">
              Free to try · No signup needed · Remembers what matters.
            </div>
          </div>
          <Link
            to="/"
            className="inline-flex items-center gap-2 rounded-full bg-[var(--rammy-cyan)] text-[var(--rammy-text)] px-5 py-3 text-sm font-semibold hover:opacity-90 transition shrink-0"
            data-testid="share-cta"
          >
            Try Rammy <L.ArrowRight size={14}/>
          </Link>
        </div>
      </div>
    </div>
  );
}
